import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import attendanceService from "../../services/attendanceService";
import courseService from "../../services/courseService";
import { getYearOptions } from "../../utils/courseYears";

const STATUS_OPTIONS = [
  "Present",
  "Absent",
  "Late",
  "On Leave",
  "Medical Leave",
  "Half Day",
];

const STATUS_STYLES = {
  Present: { bg: "#dcfce7", color: "#166534" },
  Absent: { bg: "#fee2e2", color: "#b91c1c" },
  Late: { bg: "#fef3c7", color: "#b45309" },
  "On Leave": { bg: "#e0f2fe", color: "#0369a1" },
  "Medical Leave": { bg: "#ede9fe", color: "#6d28d9" },
  "Half Day": { bg: "#fce7f3", color: "#be185d" },
};

const getToday = () => new Date().toISOString().slice(0, 10);

export default function MarkAttendance() {
  const { academicYear, user } = useApp();
  const [date, setDate] = useState(getToday);
  const [course, setCourse] = useState("");
  const [yearNumber, setYearNumber] = useState("");
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [markedBy, setMarkedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [courses, setCourses] = useState([]);

  const selectedCourse = useMemo(
    () => courses.find((c) => c._id === course),
    [courses, course],
  );

  const yearOptions = useMemo(() => {
    return selectedCourse ? getYearOptions(selectedCourse) : [];
  }, [selectedCourse]);

  useEffect(() => {
    setMarkedBy(user?.displayName || user?.username || "");
  }, [user]);

  useEffect(() => {
    courseService
      .getAll({ isActive: true })
      .then((res) => {
        const rows = res.data || [];
        setCourses(rows);
        if (rows.length) {
          setCourse((current) =>
            rows.some((c) => c._id === current) ? current : rows[0]._id,
          );
        }
      })
      .catch((err) => toast.error("Couldn't load courses: " + err.message));
  }, []);

  useEffect(() => {
    if (!yearOptions.length) {
      setYearNumber("");
      return;
    }
    setYearNumber((current) =>
      yearOptions.some((y) => String(y.yearNumber) === String(current))
        ? current
        : String(yearOptions[0].yearNumber),
    );
  }, [yearOptions]);

  useEffect(() => {
    if (!academicYear?._id || !date || !course || !yearNumber) return;

    setLoading(true);
    attendanceService
      .getMarkingSheet({
        academicYear: academicYear._id,
        attendanceDate: date,
        course,
        yearNumber,
        search,
      })
      .then((res) => {
        const rows = res.data || [];
        setStudents(rows);
        setMarkedBy((current) => res.meta?.markedBy || current || user?.displayName || "");
        setNotes(res.meta?.notes || "");
        setUpdatedAt(res.meta?.updatedAt || null);

        const nextMap = {};
        rows.forEach((student) => {
          nextMap[student._id] = {
            status: student.attendance?.status || "",
            remarks: student.attendance?.remarks || "",
          };
        });
        setAttendanceMap(nextMap);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [academicYear, date, course, yearNumber, search, user]);

  const handleStatusChange = (studentId, value) => {
    setAttendanceMap((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || { remarks: "" }),
        status: value,
      },
    }));
  };

  const handleRemarksChange = (studentId, value) => {
    setAttendanceMap((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || { status: "" }),
        remarks: value,
      },
    }));
  };

  const applyBulkStatus = (status) => {
    setAttendanceMap((prev) => {
      const next = { ...prev };
      students.forEach((student) => {
        next[student._id] = {
          ...(next[student._id] || { remarks: "" }),
          status,
        };
      });
      return next;
    });
  };

  const summary = useMemo(() => {
    const counts = STATUS_OPTIONS.reduce(
      (acc, status) => ({ ...acc, [status]: 0 }),
      {},
    );

    let unmarked = 0;
    students.forEach((student) => {
      const status = attendanceMap[student._id]?.status || "";
      if (!status) unmarked += 1;
      else counts[status] += 1;
    });

    return { counts, unmarked };
  }, [attendanceMap, students]);

  const handleSave = async () => {
    const records = students
      .map((student) => ({
        student: student._id,
        status: attendanceMap[student._id]?.status || "",
        remarks: attendanceMap[student._id]?.remarks || "",
      }))
      .filter((record) => record.status);

    if (!records.length) {
      toast.error("Please mark attendance for at least one student.");
      return;
    }

    setSaving(true);
    try {
      await attendanceService.markAttendance({
        academicYear: academicYear._id,
        attendanceDate: date,
        course,
        yearNumber,
        markedBy,
        notes,
        records,
      });
      setUpdatedAt(new Date().toISOString());
      toast.success("Attendance saved successfully.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 className="page-title">Mark Attendance</h1>
          <p className="page-subtitle">Mark daily attendance by course and class year.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-outline" onClick={() => applyBulkStatus("Present")}>
            Mark All Present
          </button>
          <button type="button" className="btn btn-outline" onClick={() => applyBulkStatus("Absent")}>
            Mark All Absent
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || !academicYear}>
            {saving ? "Saving..." : "Save Attendance"}
          </button>
        </div>
      </div>

      {!academicYear ? (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", fontSize: 13, color: "#92400e" }}>
          Select an academic year from the Dashboard before marking attendance.
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: "1rem" }}>
            <div className="card-body">
              <div className="form-grid" style={{ alignItems: "end" }}>
                <div className="form-group">
                  <label className="form-label">Attendance Date</label>
                  <input className="form-control" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Course</label>
                  <select className="form-control" value={course} onChange={(e) => {
                    setCourse(e.target.value);
                    setYearNumber("");
                  }}>
                    <option value="">Select Course</option>
                    {courses.map((c) => (
                      <option key={c._id} value={c._id}>{c.shortName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Class Year</label>
                  <select className="form-control" value={yearNumber} onChange={(e) => setYearNumber(e.target.value)} disabled={!course}>
                    <option value="">Select Year</option>
                    {yearOptions.map((y) => (
                      <option key={y.yearNumber} value={y.yearNumber}>{y.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Marked By</label>
                  <input className="form-control" value={markedBy} onChange={(e) => setMarkedBy(e.target.value)} placeholder="Admin / Faculty name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Search Student</label>
                  <input className="form-control" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, enrollment, mobile..." />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional note for the day: seminar, practical, holiday adjustment..." />
                </div>
              </div>
              {updatedAt && (
                <div style={{ marginTop: "0.75rem", fontSize: 12, color: "var(--text-muted)" }}>
                  Last updated: {new Date(updatedAt).toLocaleString("en-IN")}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
            <div className="card"><div className="card-body" style={{ padding: "1rem" }}><div style={{ fontSize: 12, color: "var(--text-muted)" }}>Students Loaded</div><div style={{ fontSize: 24, fontWeight: 700 }}>{students.length}</div></div></div>
            <div className="card"><div className="card-body" style={{ padding: "1rem" }}><div style={{ fontSize: 12, color: "var(--text-muted)" }}>Unmarked</div><div style={{ fontSize: 24, fontWeight: 700, color: "#b45309" }}>{summary.unmarked}</div></div></div>
            {STATUS_OPTIONS.map((status) => (
              <div key={status} className="card">
                <div className="card-body" style={{ padding: "1rem" }}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{status}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: STATUS_STYLES[status].color }}>{summary.counts[status]}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            {loading ? (
              <div className="loading-center"><div className="spinner" />Loading attendance sheet...</div>
            ) : students.length === 0 ? (
              <div className="empty-state">
                <p>No students found for the selected filters.</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>Enrollment No.</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, index) => {
                      const status = attendanceMap[student._id]?.status || "";
                      const style = STATUS_STYLES[status];

                      return (
                        <tr key={student._id}>
                          <td>{index + 1}</td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{student.fullName}</div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                              {student.course?.shortName || student.course?.courseName || "Course"} {student.yearNumber ? `· Year ${student.yearNumber}` : ""}
                            </div>
                          </td>
                          <td><span className="badge badge-blue">{student.enrollmentNo}</span></td>
                          <td>{student.mobile || "—"}</td>
                          <td style={{ minWidth: 180 }}>
                            <select
                              className="form-control"
                              value={status}
                              onChange={(e) => handleStatusChange(student._id, e.target.value)}
                              style={style ? { background: style.bg, color: style.color, borderColor: style.bg, fontWeight: 600 } : undefined}
                            >
                              <option value="">Select Status</option>
                              {STATUS_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ minWidth: 220 }}>
                            <input
                              className="form-control"
                              value={attendanceMap[student._id]?.remarks || ""}
                              onChange={(e) => handleRemarksChange(student._id, e.target.value)}
                              placeholder="Optional note"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
